import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Loader2, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";
const EMPTY = { title: "", text: "", link_url: "", link_label: "" };

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminNews({ token }) {
  const [news, setNews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/news`);
    setNews(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/news/${editingId}`, form, authHeaders());
        toast.success("Neuigkeit aktualisiert");
      } else {
        await axios.post(`${API}/news`, form, authHeaders());
        toast.success("Neuigkeit veröffentlicht", { description: "Sie ist sofort auf der Website sichtbar." });
      }
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Speichern fehlgeschlagen", { description: typeof detail === "string" ? detail : "Bitte erneut versuchen." });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({ title: n.title, text: n.text, link_url: n.link_url || "", link_label: n.link_label || "" });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const remove = async (n) => {
    if (!window.confirm(`„${n.title}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/news/${n.id}`, authHeaders());
      toast.success("Neuigkeit gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <section className="mt-12" data-testid="admin-news">
      <div className="flex items-center gap-3 mb-5">
        <Megaphone size={20} className="text-[#1A382B]" />
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">Neuigkeiten verwalten</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <form onSubmit={save} className="rounded-2xl border border-[#D8D3C5] bg-white p-6 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="news-form">
            <h3 className="font-display text-xl font-semibold text-[#1A382B]">
              {editingId ? "Neuigkeit bearbeiten" : "Neue Neuigkeit"}
            </h3>
            <div className="mt-5 space-y-4">
              <div>
                <label className={LABEL}>Überschrift *</label>
                <input required className={FIELD} value={form.title} onChange={set("title")} placeholder="z. B. Wasseranstellung im Frühjahr" data-testid="news-title-input" />
              </div>
              <div>
                <label className={LABEL}>Kurztext * (ca. 2 Zeilen)</label>
                <textarea required rows={3} maxLength={400} className={FIELD} value={form.text} onChange={set("text")} placeholder="Kurze Info mit dem Wichtigsten …" data-testid="news-text-input" />
              </div>
              <div>
                <label className={LABEL}>Link für nähere Informationen</label>
                <input type="url" className={FIELD} value={form.link_url} onChange={set("link_url")} placeholder="https://…" data-testid="news-link-input" />
              </div>
              <div>
                <label className={LABEL}>Link-Beschriftung</label>
                <input className={FIELD} value={form.link_label} onChange={set("link_label")} placeholder="Mehr erfahren" data-testid="news-linklabel-input" />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button type="submit" disabled={saving} data-testid="news-submit-button" className="inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-6 py-3 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                {saving && <Loader2 size={15} className="animate-spin" />}
                {editingId ? "Speichern" : "Veröffentlichen"}
              </button>
              {editingId && (
                <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} data-testid="news-cancel-edit-button" className="text-sm font-medium text-[#6F7E73] hover:text-[#B93829]">
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>
        <div className="lg:col-span-3">
          {news.length === 0 && (
            <p className="rounded-2xl border border-[#D8D3C5] bg-white p-6 text-sm text-[#47544A]" data-testid="news-empty">
              Noch keine Neuigkeiten veröffentlicht. Der Bereich erscheint erst auf der Website, sobald die erste Neuigkeit angelegt ist.
            </p>
          )}
          <div className="space-y-3" data-testid="admin-news-list">
            {news.map((n, i) => (
              <div key={n.id} className="flex items-start gap-4 rounded-2xl border border-[#D8D3C5] bg-white p-5" data-testid={`admin-news-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-jbm text-xs tracking-wider uppercase text-[#5A826B]">{formatDateTime(n.created_at)}</p>
                  <p className="font-semibold text-[#1A382B]">{n.title}</p>
                  <p className="mt-1 text-sm text-[#47544A] line-clamp-2">{n.text}</p>
                  {n.link_url && <p className="mt-1 text-xs text-[#6F7E73] truncate">Link: {n.link_url}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => startEdit(n)} data-testid={`news-edit-${i}`} aria-label="Bearbeiten" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#1A382B] hover:border-[#5A826B] transition-colors">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove(n)} data-testid={`news-delete-${i}`} aria-label="Löschen" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#B93829] hover:border-[#B93829] transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
